import React, { FC, useCallback, useEffect, useState } from "react";
import { apiCalls, EventWithContext } from "../../../scripts/api";
import { DEV_MODE } from "../../../scripts/devMode";
import { IAllGames, IElectionData, IPlayerCollections, ISnapshotData, ITeamCollections, Processors } from "../../../scripts/dataProcessors";
import { BlaseballEvent, ElectionEvent, ForbiddenBook, GameEvent, SimModel } from "../../../scripts/models/models";



type LoadedData = {
    simModel?: SimModel,
    allGames?: IAllGames,
    teamCollections?: ITeamCollections,
    playerCollections?: IPlayerCollections,
    snapshots?: ISnapshotData[]
    book?: ForbiddenBook,
    electionData?: IElectionData,
    events?: BlaseballEvent[],
    eventsWithTeamStates?: EventWithContext[]
}

export const DataContext = React.createContext<LoadedData>({})

const DataLoader: FC = (props) => {
    const { children } = props;

    // Sim data
    const [simModel, setSimData] = useState<SimModel>();

    const [teamCollections, setTeamCollections] = useState<ITeamCollections>()
    const [allGames, setAllGames] = useState<IAllGames>();
    const [playerCollections, setPlayerCollections] = useState<IPlayerCollections>();
    const [book, setBook] = useState<ForbiddenBook>()
    const [electionData, setElectionData] = useState<IElectionData>()
    const [gameEvents, setGameEvents] = useState<GameEvent[]>()
    const [electionEvents, setElectionEvents] = useState<ElectionEvent[]>()
    const [eventsWithTeamStates, setEventWithTeamStates] = useState<EventWithContext[]>()

    const [snapshots, setSnapshots] = useState<ISnapshotData[]>()

    const loadAllData = useCallback(() => {

        // Load sim data
        apiCalls.simulationData().then((simData) => {
            setSimData(simData)


            // Load teams from SIBR
            apiCalls.teams().then((allTeams) => {
                setTeamCollections(Processors.buildTeamCollections(allTeams))

                // Load SIBR Data
                // All games
                apiCalls.games().then((allGames) => {
                    const gamesData = (Processors.buildGames(allTeams, allGames, simData))
                    setAllGames(gamesData);

                    // All players
                    apiCalls.players().then((allPlayers) => {
                        setPlayerCollections(Processors.buildPlayerCollections(allTeams, allPlayers));

                        // Load data from snapshots
                        apiCalls.snapshots().then((data) => {

                            setSnapshots(Processors.buildSnapshotsData(data, allTeams, gamesData));
                        });

                    });
                })


                if (DEV_MODE) {
                    // Load election info
                    apiCalls.electionInfo().then((fetchedData) => {
                        const { elections, preferences } = fetchedData;
                        setElectionData(Processors.buildElectionData(allTeams, elections, preferences))

                    });
                }
            });


        });

        // Load the book
        apiCalls.book().then((book) => {
            setBook(book)
        })

        // Load events
        apiCalls.gameEvents().then((events) => {
            setGameEvents(events)
        })

        // TODO election events
        apiCalls.electionEvents().then(events => {
            setElectionEvents(events)
        })

    }, [])

    // Load data
    useEffect(() => {
        loadAllData()
    }, [loadAllData])


    const [hasLoadedEventContext, setHasLoadedEventContext] = useState<boolean>(false)

    useEffect(() => {
        if (!!gameEvents && !!electionEvents && !!allGames && !!simModel && !!playerCollections && !!teamCollections && !hasLoadedEventContext) {
            setHasLoadedEventContext(true);

            apiCalls.teamDataForEvents(gameEvents, electionEvents, allGames, teamCollections.allTeams, playerCollections.allPlayers).then((data) => {
                setEventWithTeamStates(data)
            })
        }
    }, [gameEvents, electionEvents, allGames, simModel, hasLoadedEventContext, playerCollections, teamCollections])


    let events: BlaseballEvent[] = []
    if (gameEvents) {
        events = events.concat(gameEvents)
    }
    if (electionEvents) {
        events = events.concat(electionEvents)
    }

    const data: LoadedData = {
        simModel,
        teamCollections,
        allGames: allGames,
        playerCollections,
        snapshots,
        book,
        electionData,
        events,
        eventsWithTeamStates
    }

    return <DataContext.Provider value={data}>
        {children}
    </DataContext.Provider>
}

export default DataLoader;